import { useEffect, FC, PropsWithChildren, useRef } from "react";

import * as amplitude from "@amplitude/analytics-browser";
import { sessionReplayPlugin } from "@amplitude/plugin-session-replay-browser";
import { useCookiebotConsent } from "@hooks/useCookiebotConsent";
import { useGetUserDetailsQuery } from "@store/services/user";
import { getIsProductionEnvironment } from "@utils";

import { AmplitudeContext } from "./AmplitudeContext";

const AmplitudeProvider: FC<PropsWithChildren> = ({ children }) => {
  const isAmplitudeInitialized = useRef(false);
  const { data: userDetailsData } = useGetUserDetailsQuery();
  const { isStatisticsConsentGiven } = useCookiebotConsent();

  useEffect(() => {
    const isUserImpersonated = userDetailsData?.id != null && userDetailsData?.impersonated;

    // Amplitude is already initialized; keep tracking in sync with consent and
    // impersonation so withdrawing statistics consent (or an impersonation
    // starting mid-session) stops further events
    if (isAmplitudeInitialized.current) {
      amplitude.setOptOut(!isStatisticsConsentGiven || isUserImpersonated);
      return;
    }

    // No statistics consent (yet), do not initialize
    if (!isStatisticsConsentGiven) {
      return;
    }

    // Amplitude key is not set, do not initialize
    const amplitudeApiKey = import.meta.env.VITE_AMPLITUDE_API_KEY;
    if (!amplitudeApiKey) {
      return;
    }

    // Only initialize in production environment
    if (!getIsProductionEnvironment()) {
      return;
    }

    // User details not done loading or not available, do not initialize
    if (userDetailsData == null) {
      return;
    }

    // Do not initialize if user is impersonated
    if (isUserImpersonated) {
      return;
    }

    amplitude.add(sessionReplayPlugin());

    amplitude.init(amplitudeApiKey, {
      fetchRemoteConfig: true,
      defaultTracking: {
        sessions: true,
        pageViews: true,
        formInteractions: true,
        fileDownloads: true,
      },
      autocapture: {
        elementInteractions: true,
      },
      serverZone: "EU",
      minIdLength: 1,
    });
    isAmplitudeInitialized.current = true;
  }, [userDetailsData, isStatisticsConsentGiven]);

  return <AmplitudeContext.Provider value={amplitude}>{children}</AmplitudeContext.Provider>;
};

export default AmplitudeProvider;
